/* Rural Communities panel for the Admin Dashboard.
 *
 * Realtor counts grouped by Address city/town (the `city` tag applied during
 * Territory Import), each carrying a size tier (city | town | village |
 * community) from the bundled Canadian place reference. A tier filter narrows
 * the list; clicking a community opens a dialog with its realtors (linked to
 * their profiles). Scoped server-side to the admin's current location view.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { MapPinned, Loader2, Users, Building2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const TIERS = [
  { key: "", label: "All" },
  { key: "city", label: "Cities" },
  { key: "town", label: "Towns" },
  { key: "village", label: "Villages" },
  { key: "community", label: "Communities" },
];

const TIER_BADGE = {
  city: "bg-red-50 text-[#C8102E] border-red-200",
  town: "bg-amber-50 text-amber-700 border-amber-200",
  village: "bg-sky-50 text-sky-700 border-sky-200",
  community: "bg-slate-100 text-slate-600 border-slate-200",
};

export default function RuralCommunitiesCard() {
  const [tier, setTier] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null); // { city }
  const [realtors, setRealtors] = useState(null);
  const [rLoading, setRLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/admin/rural-communities", {
          params: tier ? { tier } : {},
        });
        if (alive) setData(data);
      } catch (e) {
        if (alive)
          toast.error("Failed to load rural communities", {
            description: formatApiError(e),
          });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [tier]);

  const openCity = async (city) => {
    setActive({ city });
    setRealtors(null);
    setRLoading(true);
    try {
      const { data } = await api.get("/admin/rural-communities/realtors", {
        params: { city },
      });
      setRealtors(data.realtors || []);
    } catch (e) {
      toast.error("Failed to load realtors", { description: formatApiError(e) });
      setRealtors([]);
    } finally {
      setRLoading(false);
    }
  };

  const communities = data?.communities || [];

  return (
    <div
      className="rounded-lg border border-slate-200 bg-white"
      data-testid="rural-communities-card"
    >
      <div className="px-4 py-3 border-b border-slate-100 flex flex-wrap items-center gap-2">
        <MapPinned className="h-4 w-4 text-slate-400" />
        <h2 className="text-base md:text-lg font-semibold text-slate-900">
          Rural communities
        </h2>
        <span className="text-xs text-slate-500">
          realtors grouped by Address city/town · scoped to your current location view
        </span>
        <span
          className="ml-auto text-xs text-slate-500 tabular-nums"
          data-testid="rural-total"
        >
          {data
            ? `${data.total_communities} communit${data.total_communities === 1 ? "y" : "ies"}`
            : ""}
        </span>
      </div>

      <div className="px-4 py-2.5 border-b border-slate-100 flex flex-wrap gap-1.5">
        {TIERS.map((t) => (
          <button
            key={t.key || "all"}
            type="button"
            onClick={() => setTier(t.key)}
            className={`rounded-full px-3 py-1 text-xs font-medium border transition-colors ${
              tier === t.key
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
            }`}
            data-testid={`rural-tier-${t.key || "all"}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="p-2">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-slate-500 p-6">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : communities.length === 0 ? (
          <p className="text-sm text-slate-500 p-6" data-testid="rural-empty">
            No realtors carry an Address city/town tag
            {tier ? ` in the “${TIERS.find((t) => t.key === tier)?.label}” tier` : ""} yet.
            Towns are tagged automatically when you import agents via Territory Import.
          </p>
        ) : (
          <div className="max-h-[420px] overflow-y-auto divide-y divide-slate-50">
            {communities.map((c) => (
              <button
                key={c.city}
                type="button"
                onClick={() => openCity(c.city)}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-slate-50 rounded-md transition-colors"
                data-testid={`rural-row-${c.city}`}
              >
                <span className="font-medium text-slate-900 flex-1 truncate">
                  {c.city}
                </span>
                <span
                  className={`text-[10px] uppercase tracking-wide font-semibold rounded px-1.5 py-0.5 border ${
                    TIER_BADGE[c.tier] || TIER_BADGE.community
                  }`}
                >
                  {c.tier}
                </span>
                <span className="inline-flex items-center gap-1 text-sm tabular-nums text-slate-600 w-20 justify-end">
                  <Users className="h-3.5 w-3.5 text-slate-400" /> {c.count}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent data-testid="rural-realtors-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPinned className="h-4 w-4 text-[#C8102E]" /> {active?.city}
              {realtors ? (
                <span className="text-sm font-normal text-slate-500">
                  · {realtors.length} realtor{realtors.length === 1 ? "" : "s"}
                </span>
              ) : null}
            </DialogTitle>
          </DialogHeader>
          {rLoading ? (
            <div className="flex items-center gap-2 text-sm text-slate-500 p-6">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading realtors…
            </div>
          ) : realtors && realtors.length > 0 ? (
            <ul className="max-h-[60vh] overflow-y-auto divide-y divide-slate-100">
              {realtors.map((r) => (
                <li key={r.id} className="py-2 flex items-center gap-2">
                  <div className="min-w-0 flex-1">
                    <Link
                      to={`/realtors/${r.id}`}
                      className="text-sm font-medium text-slate-900 hover:text-[#C8102E] hover:underline truncate block"
                      data-testid={`rural-realtor-${r.id}`}
                    >
                      {r.name || "—"}
                    </Link>
                    {r.brokerage_name ? (
                      <div className="text-xs text-slate-500 flex items-center gap-1 truncate">
                        <Building2 className="h-3 w-3 shrink-0" /> {r.brokerage_name}
                      </div>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-slate-500 p-4">
              No realtors found for this community.
            </p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
